import {
	Stack,
	Button,
	Heading,
	Inline,
	Box,
	Spinner,
	useTimeout,
	CheckboxInput,
	RadioInput,
	Text,
	Grid,
	Col,
	Divider,
	Badge,
	Icons,
} from "@sembark-travel/ui-base"
import {
	ErrorMessage,
	Form,
	FormSpy,
	GetFieldValue,
	SubmissionError,
	TextInputField,
	useFieldValue,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { extendXHRInstance, IListResponse, useXHR } from "@sembark-travel/xhr"
import { useEffect, useRef, useState } from "react"
import useSWR from "swr"

import { showSnackbar } from "@sembark-travel/ui-snackbar"
import * as Validator from "yup"
import {
	ConfirmationDialogProvider,
	ConfirmationDialog,
} from "@sembark-travel/ui-dialog"
import { ISupplier } from "./store"
import { isPrimaryRemoved } from "../Hotels"

type TMergeSuppliersFormData = {
	q?: string
	primary: ISupplier
	duplicates: Array<ISupplier>
}

const validate = validateFormValues(
	Validator.object().shape({
		duplicates: Validator.array().min(
			2,
			"Please select atleast 2 duplicate suppliers"
		),
		primary: Validator.mixed().required("Please select a primary supplier"),
	})
)

export function MergeSuppliers({
	onSuccess,
	onCancel,
}: {
	onSuccess: (primary: ISupplier) => void
	onCancel: () => void
}) {
	const initialValues: Partial<TMergeSuppliersFormData> = useRef({
		q: "",
		primary: undefined,
		duplicates: [],
	}).current
	const xhr = useXHR()
	const [mergeMoreCount, incrementMergeMoreCount] = useState<number>(0)
	return (
		<Stack gap="6">
			<Heading>Merge Duplicate Suppliers</Heading>
			<ConfirmationDialogProvider key={mergeMoreCount}>
				{({ confirm }) => (
					<Form<TMergeSuppliersFormData>
						validate={validate}
						initialValues={initialValues}
						onSubmit={withServerErrors(async (values) => {
							const { primary, duplicates } = values
							if (await confirm()) {
								const { message } = await xhr
									.post<{ message: string }>(
										"/transport-service-providers/merge",
										{
											primary: primary?.id,
											duplicates: duplicates.map((d) => d.id),
										}
									)
									.then((resp) => resp.data)
								showSnackbar(message)
								if (
									window.confirm(
										"Do you want to merge more duplicate suppliers ?"
									)
								) {
									incrementMergeMoreCount((count) => count + 1)
								} else {
									onSuccess(primary)
								}
							}
						})}
						subscription={{ submitting: true }}
					>
						{({ submitting, handleSubmit }) => (
							<form onSubmit={handleSubmit}>
								<Stack gap="4">
									<Grid gap="8">
										<Col sm={12} md>
											<Stack gap="4" position="relative">
												<Stack
													gap="4"
													position="sticky"
													top="0"
													bgColor="default"
												>
													<Stack gap="1">
														<Heading fontSize="md">Duplicate Suppliers</Heading>
														<Text color="muted">
															Please select the duplicate suppliers
														</Text>
													</Stack>
													<TextInputField
														type="text"
														name="q"
														placeholder="Type 3 or more characters to search..."
														autoFocus
													/>
												</Stack>
												<GetFieldValue<
													TMergeSuppliersFormData["duplicates"]
												> name="duplicates">
													{({
														value: duplicateSuppliers,
														onChange: setDuplicateSuppliers,
													}) => (
														<Stack>
															<GetFieldValue<string> name="q">
																{({ value: q }) => (
																	<SuppliersList q={q}>
																		{({ suppliers }) => (
																			<Stack
																				borderWidth="1"
																				rounded="md"
																				style={{
																					maxHeight: "300px",
																					overflow: "auto",
																				}}
																			>
																				{duplicateSuppliers.map((h, index) => (
																					<Box
																						key={h.id}
																						borderTopWidth={
																							index > 0 ? "1" : undefined
																						}
																					>
																						<Inline
																							gap="4"
																							as="label"
																							paddingY="2"
																							paddingX="4"
																						>
																							<CheckboxInput
																								checked
																								onChange={() =>
																									setDuplicateSuppliers(
																										duplicateSuppliers.filter(
																											(s) => s.id !== h.id
																										)
																									)
																								}
																							/>
																							<Box flex="1">{h.name}</Box>
																						</Inline>
																					</Box>
																				))}
																				{suppliers
																					// remove selected items
																					.filter(
																						(h) =>
																							!duplicateSuppliers.find(
																								(d) => d.id === h.id
																							)
																					)
																					.map((h, index) => (
																						<Box
																							key={h.id}
																							borderTopWidth={
																								index > 0 ||
																								duplicateSuppliers.length
																									? "1"
																									: undefined
																							}
																						>
																							<Inline
																								gap="4"
																								as="label"
																								paddingY="2"
																								paddingX="4"
																							>
																								<CheckboxInput
																									checked={false}
																									onChange={() =>
																										setDuplicateSuppliers(
																											duplicateSuppliers.concat(
																												[h]
																											)
																										)
																									}
																								/>
																								<Box flex="1">
																									<SupplierItem supplier={h} />
																								</Box>
																							</Inline>
																						</Box>
																					))}
																			</Stack>
																		)}
																	</SuppliersList>
																)}
															</GetFieldValue>
														</Stack>
													)}
												</GetFieldValue>
												<ErrorMessage name="duplicates" />
											</Stack>
										</Col>
										<Col sm={12} md={6}>
											<SelectPrimarySupplierField />
										</Col>
									</Grid>
									<Divider sm />
									<SubmissionError />
									<Inline gap="4">
										<Button type="submit" disabled={submitting}>
											{submitting ? "Merging..." : "Merge Suppliers"}
										</Button>
										<Button onClick={() => onCancel()} disabled={submitting}>
											Cancel
										</Button>
									</Inline>
									<ConfirmationDialog
										title="Merge Suppliers Confirmation"
										acceptActionLabel="Yes, Merge these suppliers"
									>
										<Stack gap="6">
											<FormSpy<TMergeSuppliersFormData>
												subscription={{ values: true }}
											>
												{({ values }) => (
													<Stack borderWidth="1" rounded="md" overflow="hidden">
														{[values.primary]
															.concat(
																values.duplicates.filter(
																	(d) => d.id !== values.primary.id
																)
															)
															.map((d, i) => {
																const isPrimary = d.id === values.primary.id
																return (
																	<Box
																		key={d.id}
																		bgColor={isPrimary ? "primary" : undefined}
																		padding="2"
																		borderTopWidth={i > 0 ? "1" : undefined}
																	>
																		<Stack gap="1">
																			{isPrimary ? (
																				<Text>
																					<Badge primary>
																						Primary Supplier
																					</Badge>
																				</Text>
																			) : null}
																			<SupplierItem supplier={d} />
																		</Stack>
																	</Box>
																)
															})}
													</Stack>
												)}
											</FormSpy>
											<Stack gap="2">
												<Heading fontSize="md" color="warning">
													Merging Suppliers Will :
												</Heading>
												<Stack
													gap="2"
													as="ul"
													paddingLeft="4"
													listStyleType="disc"
												>
													{[
														"Replace all existing usage of duplicates with primary Suppliers (quotes, supplier bookings, payments and accounting)",
														"Disable duplicate suppliers",
													].map((result) => (
														<Box as="li" key={result}>
															<Text>{result}</Text>
														</Box>
													))}
													<Box as="li" color="warning">
														<Icons.AttentionSolid /> This is an irreversible
														process.
													</Box>
												</Stack>
											</Stack>
											<Text fontWeight="semibold" color="warning">
												Are you sure you want to continue ?
											</Text>
										</Stack>
									</ConfirmationDialog>
								</Stack>
							</form>
						)}
					</Form>
				)}
			</ConfirmationDialogProvider>
		</Stack>
	)
}

function SelectPrimarySupplierField() {
	const { value: duplicateSuppliers } =
		useFieldValue<TMergeSuppliersFormData["duplicates"]>("duplicates")
	const { value: primary_supplier, onChange: changePrimarySupplier } =
		useFieldValue<TMergeSuppliersFormData["primary"]>("primary")

	useEffect(() => {
		if (primary_supplier) {
			const getId = (item: typeof primary_supplier) => item.id
			if (isPrimaryRemoved(primary_supplier, duplicateSuppliers, getId)) {
				changePrimarySupplier(undefined)
			}
		}
	}, [primary_supplier, changePrimarySupplier, duplicateSuppliers])

	return (
		<Stack gap="4" position="relative">
			<Stack gap="4" position="sticky" bgColor="default" top="0" zIndex="10">
				<Stack gap="1">
					<Heading fontSize="md">Primary/Master Supplier</Heading>
					<Text color="muted">
						Please select the primary/master supplier of duplicates
					</Text>
				</Stack>
			</Stack>
			{duplicateSuppliers.length <= 1 ? (
				<Box padding="4" bgColor="inset" rounded="md">
					<Text>
						Please select some duplicates to select a primary supplier
					</Text>
				</Box>
			) : (
				<Stack borderWidth="1" rounded="md">
					{duplicateSuppliers.map((h, index) => {
						const selected = h.id === primary_supplier?.id
						return (
							<Box key={h.id} borderTopWidth={index > 0 ? "1" : undefined}>
								<Inline
									gap="4"
									as="label"
									paddingY="2"
									paddingX="4"
									bgColor={selected ? "primary" : { hover: "subtle" }}
								>
									<RadioInput
										checked={selected}
										onChange={(e) => {
											const checked = e.currentTarget.checked
											changePrimarySupplier(checked ? h : undefined)
										}}
									/>
									<Box flex="1">
										<SupplierItem supplier={h} />
									</Box>
								</Inline>
							</Box>
						)
					})}
				</Stack>
			)}
			<ErrorMessage name="primary" />
		</Stack>
	)
}

function SuppliersList({
	q: propsQ,
	children,
}: {
	q: string
	children: (props: { suppliers: Array<ISupplier> }) => React.ReactNode
}) {
	const [q, setQ] = useState(propsQ)
	const { set: setTimeout, clear: clearTimeout } = useTimeout()
	useEffect(() => {
		setTimeout(() => {
			setQ(propsQ)
		}, 1000)
		return clearTimeout
	}, [propsQ, setTimeout, clearTimeout])
	const xhr = useXHR()
	const abortController = useRef<AbortController>()
	const { data: resp } = useSWR(
		q.length > 2 ? `/transport-service-providers?${q}` : null,
		async () => {
			// abort any pending requests
			abortController.current?.abort()
			abortController.current = new AbortController()
			const apiInstance = extendXHRInstance(xhr, {
				signal: abortController.current.signal,
			})
			return apiInstance
				.get<
					IListResponse<ISupplier>
				>(`/transport-service-providers`, { params: { q, limit: 10 } })
				.then((resp) => resp.data)
		}
	)
	if (q.length <= 2) {
		return <>{children({ suppliers: [] })}</>
	}
	if (!resp) {
		return <Spinner padding="4" alignCenter />
	}
	const { data } = resp
	return <>{children({ suppliers: data })}</>
}

function SupplierItem({ supplier }: { supplier: ISupplier }) {
	const { name } = supplier
	return (
		<Inline gap="2" justifyContent="between" fontWeight="normal">
			<Stack gap="px">
				<Text fontWeight="semibold">{name}</Text>
			</Stack>
		</Inline>
	)
}
